/* /src/mitras/useMitraSeo.js
 *
 * Vanilla DOM SEO updater for the Mitra ecosystem. Sets:
 *   • <title>
 *   • <meta name="description">
 *   • <meta name="keywords">
 *   • Open Graph: og:title, og:description, og:url, og:type
 *   • <link rel="canonical">
 *   • <script type="application/ld+json"> with LocalBusiness +
 *     BreadcrumbList schemas
 *
 * Hooks into per-bucket / per-city pages without pulling in
 * react-helmet — Mera Naksha already has zero SSR, so DOM updates
 * via useEffect are sufficient for crawler discovery (Google + Bing
 * both execute client JS).
 *
 * The hook is intentionally cleanup-free for meta/canonical updates:
 * subsequent route changes overwrite the same tags, and we never want
 * stale tags during the SPA transition.
 */
import { useEffect } from "react";

const JSON_LD_ID = "mitra-jsonld";
const ORG_LD_ID = "mitra-org-jsonld";
const SITE_NAME = "Mera Naksha";
const SITE_ORIGIN =
  (typeof window !== "undefined" && window.location?.origin) ||
  "https://meranaksha.com";

const _upsertMeta = (selector, attrs) => {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => {
      if (k !== "content") el.setAttribute(k, v);
    });
    document.head.appendChild(el);
  }
  if (attrs.content !== undefined) el.setAttribute("content", attrs.content);
};

const _upsertLink = (rel, href) => {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const _upsertJsonLd = (id, payload) => {
  if (typeof document === "undefined") return;
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
};

/**
 * Set per-bucket / per-page SEO. Pass everything (no defaults) so the
 * caller is explicit. `bucket` is optional — when present we render
 * a LocalBusiness schema with the bucket's services as `makesOffer`.
 *
 * @param {object} opts
 * @param {string} opts.title — full <title>
 * @param {string} opts.description — meta description (≤160 chars)
 * @param {string} opts.path — relative path for canonical (e.g. "/mitras/design")
 * @param {object} [opts.bucket] — MITRA_BUCKETS entry (see mitraCategories.jsx)
 * @param {Array<{label,href}>} [opts.breadcrumbs] — for BreadcrumbList LD
 */
export const useMitraSeo = ({ title, description, path, bucket, breadcrumbs }) => {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const url = `${SITE_ORIGIN}${path || "/"}`;
    document.title = title;

    _upsertMeta('meta[name="description"]', { name: "description", content: description });
    _upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
    _upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
    _upsertMeta('meta[property="og:url"]', { property: "og:url", content: url });
    _upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    _upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: SITE_NAME });
    _upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    _upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    _upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });

    if (bucket?.services?.length) {
      const keywords = [bucket.label, ...bucket.services, "Mera Naksha", "Bharat", "India"].join(", ");
      _upsertMeta('meta[name="keywords"]', { name: "keywords", content: keywords });
    }

    _upsertLink("canonical", url);

    // Structured data — Organization (always) + LocalBusiness (per bucket) +
    // BreadcrumbList (when provided).
    _upsertJsonLd(ORG_LD_ID, {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_ORIGIN,
      logo: `${SITE_ORIGIN}/logo.png`,
      sameAs: [
        "https://www.instagram.com/meranaksha",
        "https://www.facebook.com/meranaksha",
      ],
    });

    const ld = bucket
      ? {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: `${bucket.label} on ${SITE_NAME}`,
          description,
          url,
          areaServed: { "@type": "Country", name: "India" },
          serviceType: bucket.services,
          parentOrganization: { "@type": "Organization", name: SITE_NAME, url: SITE_ORIGIN },
        }
      : {
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: SITE_NAME,
          url: SITE_ORIGIN,
          potentialAction: {
            "@type": "SearchAction",
            target: `${SITE_ORIGIN}/mitras?q={search_term_string}`,
            "query-input": "required name=search_term_string",
          },
        };

    if (breadcrumbs?.length) {
      _upsertJsonLd(JSON_LD_ID, [
        ld,
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbs.map((b, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: b.label,
            item: `${SITE_ORIGIN}${b.href}`,
          })),
        },
      ]);
    } else {
      _upsertJsonLd(JSON_LD_ID, ld);
    }
  }, [title, description, path, bucket, breadcrumbs]);
};
